/**
 * Sign-in screen modeled on Microsoft's enterprise Xbox card.
 *
 * Layout: brand mark centered at top, "Sign in" heading, contextual
 * subtitle, email field, trouble-signing-in link, full-width green
 * Next button, footer note. Background is a slow green→black animated
 * gradient layer (`.kse-login-bg`) defined in theme/layout.css.
 *
 * The email entered here is passed to MSAL as `loginHint` so Microsoft's
 * own page is pre-populated and the user goes straight to password / MFA.
 */
import { useState, type CSSProperties } from "react";
import { useMsal } from "@azure/msal-react";

const logoUrl = "/kse-logo.png";

const PRIMARY_GREEN = "#0E8A37";       // Xbox-card "Next" button green
const PRIMARY_GREEN_HOVER = "#0A7A2F";
const LINK_GREEN = "#7FBE5C";          // accent for inline links
const CARD_BG = "#1E1E1E";
const CARD_BORDER = "rgba(255,255,255,0.06)";

// Slow animated gradient backdrop. The component below renders a layer
// using the className `kse-login-bg` whose keyframes (defined in
// theme/layout.css) drift between several green-to-black stops. We use
// a class instead of inline style because @keyframes can't be inline.
const pageStyle: CSSProperties = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  fontFamily:
    "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
  overflow: "hidden",
};

const cardStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 480,
  background: CARD_BG,
  borderRadius: 8,
  border: `1px solid ${CARD_BORDER}`,
  boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
  padding: "28px 56px 36px",
  color: "#ffffff",
  boxSizing: "border-box",
};

const logoRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: 4,
  marginBottom: 28,
};

const headingStyle: CSSProperties = {
  fontSize: 30,
  fontWeight: 700,
  margin: "0 0 6px",
  textAlign: "center",
  letterSpacing: 0.1,
};

const subtitleStyle: CSSProperties = {
  margin: "0 0 28px",
  textAlign: "center",
  fontSize: 15,
  color: "#e6e6e6",
};

const inputStyle: CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 14px",
  background: "transparent",
  color: "#ffffff",
  border: "1px solid #6f6f6f",
  borderRadius: 4,
  fontSize: 15,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const linkStyle: CSSProperties = {
  display: "inline-block",
  marginTop: 14,
  fontSize: 14,
  color: LINK_GREEN,
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textDecoration: "none",
};

const buttonStyle: CSSProperties = {
  width: "100%",
  height: 48,
  marginTop: 28,
  background: PRIMARY_GREEN,
  color: "#ffffff",
  border: "none",
  borderRadius: 4,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  transition: "background-color 120ms ease",
};

const footerStyle: CSSProperties = {
  marginTop: 28,
  paddingTop: 18,
  borderTop: `1px solid ${CARD_BORDER}`,
  textAlign: "center",
  fontSize: 14,
  color: "#cfcfcf",
};

export function LoginRoute({ apiScope }: { apiScope: string }) {
  const { instance } = useMsal();
  const [email, setEmail] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    void instance
      .loginRedirect({
        scopes: [apiScope],
        // If the user typed an email, pre-fill it on Microsoft's side so
        // they go straight to password / MFA. If empty, MSAL prompts them.
        loginHint: email.trim() || undefined,
        prompt: email.trim() ? undefined : "select_account",
      })
      .catch(() => setSigningIn(false));
  };

  return (
    <div style={pageStyle}>
      {/* Slow animated green→black gradient. Lives behind the card via
          z-index 0 / 1 layering — the card stays sharp while the
          backdrop drifts. */}
      <div className="kse-login-bg" aria-hidden />

      <form style={{ ...cardStyle, zIndex: 1 }} onSubmit={onSubmit} autoComplete="on">
        <div style={logoRowStyle}>
          <img
            src={logoUrl}
            alt="KS Environmental"
            style={{ height: 96, width: "auto", display: "block" }}
          />
        </div>

        <h1 style={headingStyle}>Sign in</h1>
        <p style={subtitleStyle}>to continue to KSE Metadata Manager.</p>

        <input
          type="email"
          name="email"
          inputMode="email"
          autoComplete="username"
          placeholder="Email or phone number"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          style={inputStyle}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#ffffff"; }}
          onBlur={(e)  => { e.currentTarget.style.borderColor = "#6f6f6f"; }}
        />

        <button
          type="button"
          style={linkStyle}
          onClick={() =>
            window.open(
              "https://passwordreset.microsoftonline.com/",
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          Forgot your password?
        </button>

        <button
          type="submit"
          disabled={signingIn}
          style={{
            ...buttonStyle,
            ...(signingIn ? { opacity: 0.7, cursor: "wait" } : null),
          }}
          onMouseEnter={(e) => {
            if (!signingIn) e.currentTarget.style.backgroundColor = PRIMARY_GREEN_HOVER;
          }}
          onMouseLeave={(e) => {
            if (!signingIn) e.currentTarget.style.backgroundColor = PRIMARY_GREEN;
          }}
        >
          {signingIn ? "Redirecting…" : "Next"}
        </button>

        <div style={footerStyle}>
          New here? Contact your administrator.
        </div>
      </form>
    </div>
  );
}
